// Outside parts — batches of the SAME part bought up front from outside (15
// Seltos side mirrors) and sold one at a time.
//
// The car detail on a batch is not decoration: it is what makes the stock
// findable. An inquiry for a 2005 Seltos PMT side mirror is matched against
// these rows automatically, and the supervisor is offered the batch as a source
// next to OSM and CAP. Fill the car in as tightly as you can.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertCircle,
  FiBox,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiDollarSign,
  FiLayers,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrendingUp,
  FiX,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, Button, DeleteConfirm, PageHeader } from '../components/common/index.js';
import { outsidePartApi, stockApi } from '../api/stockApi';

const fmtMoney = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—';
};

const VARIANTS = [
  { value: '', label: 'Any variant' },
  { value: 'pmt', label: 'Petrol Manual' },
  { value: 'pat', label: 'Petrol Automatic' },
  { value: 'dmt', label: 'Diesel Manual' },
  { value: 'dat', label: 'Diesel Automatic' },
  { value: 'ev', label: 'Electric (EV)' },
];

const TABS = [
  { value: 'in_stock', label: 'In stock', hint: 'Batches with units still on the shelf' },
  { value: 'sold_out', label: 'Sold out', hint: 'Every unit has been sold' },
  { value: 'all', label: 'All', hint: 'Everything ever bought' },
];

const errText = (e, fallback) => {
  const data = e?.response?.data;
  if (typeof data?.error === 'string') return data.error;
  if (data && typeof data === 'object') {
    const first = Object.values(data)[0];
    if (Array.isArray(first) && first.length) return String(first[0]);
    if (typeof first === 'string') return first;
  }
  return e?.message || fallback;
};

const StatCard = ({ icon: Icon, label, value, tone, hint }) => {
  const tones = {
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600',
    sky: 'bg-sky-50 text-sky-600',
    emerald: 'bg-emerald-50 text-emerald-600',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="truncate text-lg font-bold text-slate-900">{value}</p>
          {hint && <p className="truncate text-[11px] text-slate-400">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, hint, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="mb-1.5 block text-sm font-semibold text-slate-700">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
  </label>
);

const inputCls =
  'w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300';

// ── Custom dropdown primitives ───────────────────────────────────────────────
// The browser's native <select>/<datalist> can't be styled and looks nothing
// like the rest of the panel, so these two do the job properly: same rounded
// borders, violet focus ring and shadowed popover as every other control.

/** Close me when a click lands outside, or Escape is pressed. */
const useDismiss = (open, close) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
};

const menuCls =
  'absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-100 bg-white py-1 shadow-xl';

/** Money / quantity input.
 *
 * Deliberately NOT type="number": a native number input silently changes value
 * when the wheel scrolls over it or an arrow key is pressed, so a typed 5000
 * can become 4998 without anyone touching it. This one takes digits (and one
 * decimal point) as plain text — what you type is what gets saved.
 */
const NumberInput = ({ value, onChange, disabled, placeholder, decimals = true, className = '' }) => (
  <input
    type="text"
    inputMode={decimals ? 'decimal' : 'numeric'}
    value={value}
    disabled={disabled}
    placeholder={placeholder}
    onChange={(e) => {
      let v = e.target.value.replace(decimals ? /[^0-9.]/g : /[^0-9]/g, '');
      if (decimals) {
        const bits = v.split('.');
        if (bits.length > 2) v = `${bits[0]}.${bits.slice(1).join('')}`;
      }
      onChange(v);
    }}
    className={className || inputCls}
  />
);

/** Styled replacement for <select> — options: [{value, label}] */
const Select = ({ value, options, onChange, disabled, placeholder = 'Select…' }) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const current = options.find((o) => o.value === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between gap-2 rounded-xl border bg-white px-3 py-2 text-left text-sm transition disabled:opacity-60 ${
          open ? 'border-violet-400 ring-2 ring-violet-200' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <span className={current ? 'text-slate-900' : 'text-slate-400'}>
          {current ? current.label : placeholder}
        </span>
        <FiChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={menuCls} role="listbox">
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => { onChange(o.value); close(); }}
                className={`flex w-full items-center justify-between gap-2 px-3.5 py-2 text-left text-sm transition ${
                  active ? 'bg-violet-50 font-bold text-violet-700' : 'font-semibold text-slate-700 hover:bg-slate-50'
                }`}
              >
                {o.label}
                {active && <FiCheck className="h-3.5 w-3.5" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Type-or-pick combo — free text with a styled suggestion list. */
const ComboInput = ({ value, options, onChange, disabled, placeholder, numeric = false }) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);

  const shown = useMemo(() => {
    const q = String(value || '').trim();
    if (!q) return options;
    return options.filter((o) => String(o).startsWith(q));
  }, [value, options]);

  return (
    <div className="relative" ref={ref}>
      <div
        className={`flex items-center rounded-xl border bg-white transition ${
          open ? 'border-violet-400 ring-2 ring-violet-200' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <input
          type="text"
          inputMode={numeric ? 'numeric' : 'text'}
          value={value}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            const v = numeric ? e.target.value.replace(/[^0-9]/g, '') : e.target.value;
            onChange(v);
            setOpen(true);
          }}
          placeholder={placeholder}
          className="w-full rounded-xl bg-transparent px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:opacity-60"
        />
        {value ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => { onChange(''); close(); }}
            className="px-2 text-slate-300 hover:text-rose-500"
            aria-label="Clear"
          >
            <FiX className="h-3.5 w-3.5" />
          </button>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen((v) => !v)}
            className="px-2.5 text-slate-400"
            aria-label="Open list"
          >
            <FiChevronDown className={`h-4 w-4 transition ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      {open && shown.length > 0 && (
        <div className={menuCls} role="listbox">
          {shown.map((o) => {
            const active = String(o) === String(value);
            return (
              <button
                key={o}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => { onChange(String(o)); close(); }}
                className={`flex w-full items-center justify-between gap-2 px-3.5 py-2 text-left text-sm transition ${
                  active ? 'bg-violet-50 font-bold text-violet-700' : 'font-semibold text-slate-700 hover:bg-slate-50'
                }`}
              >
                {o}
                {active && <FiCheck className="h-3.5 w-3.5" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Car search picker ────────────────────────────────────────────────────────
// One box over the whole catalogue — type "Po" and pick "Polo · Volkswagen",
// or pick a bare brand ("Kia — every model"), or keep what you typed as a
// custom car. Picking fills brand+model; the chip's ✕ clears it back to
// "fits any car".
const CarSearchPicker = ({ catalog = [], brand, model, onPick, disabled }) => {
  const [query, setQuery] = useState('');

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { models: [], brands: [] };
    const models = [];
    const brands = [];
    for (const b of catalog) {
      if (b.name.toLowerCase().includes(q)) brands.push(b);
      for (const m of b.models || []) {
        if (m.name.toLowerCase().includes(q) || b.name.toLowerCase().includes(q)) {
          models.push({ brand: b.name, model: m.name });
        }
      }
    }
    return { models: models.slice(0, 8), brands: brands.slice(0, 4) };
  }, [catalog, query]);

  const picked = brand || model;
  if (picked) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-white px-3 py-2 text-sm font-bold text-slate-800">
          {[brand, model].filter(Boolean).join(' ')}
          <button
            type="button"
            disabled={disabled}
            onClick={() => onPick('', '')}
            className="rounded-full p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
            aria-label="Clear car"
          >
            <FiX className="h-3.5 w-3.5" />
          </button>
        </span>
        {!model && <span className="text-[11px] font-semibold text-violet-600">every {brand} model</span>}
      </div>
    );
  }

  const showList = query.trim().length > 0;
  return (
    <div className="relative">
      <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search car — Polo, Swift, Kia… (leave empty = fits any car)"
        className={`${inputCls} pl-9`}
      />
      {showList && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-slate-100 bg-white py-1 shadow-xl">
          {matches.models.map((m) => (
            <button
              key={`${m.brand}-${m.model}`}
              type="button"
              onClick={() => { onPick(m.brand, m.model); setQuery(''); }}
              className="flex w-full items-baseline justify-between gap-2 px-3.5 py-2 text-left transition hover:bg-violet-50"
            >
              <span className="text-sm font-bold text-slate-800">{m.model}</span>
              <span className="text-xs text-slate-400">{m.brand}</span>
            </button>
          ))}
          {matches.brands.map((b) => (
            <button
              key={`b-${b.id}`}
              type="button"
              onClick={() => { onPick(b.name, ''); setQuery(''); }}
              className="flex w-full items-baseline justify-between gap-2 border-t border-slate-50 px-3.5 py-2 text-left transition hover:bg-violet-50"
            >
              <span className="text-sm font-bold text-slate-800">{b.name}</span>
              <span className="text-xs font-semibold text-violet-500">every model</span>
            </button>
          ))}
          {/* Not in the catalogue → keep exactly what was typed */}
          <button
            type="button"
            onClick={() => { onPick('', query.trim()); setQuery(''); }}
            className="w-full border-t border-slate-100 px-3.5 py-2 text-left text-xs font-semibold text-slate-500 transition hover:bg-slate-50"
          >
            Use “{query.trim()}” as a custom car
          </button>
        </div>
      )}
    </div>
  );
};

// ── Part search input ────────────────────────────────────────────────────────
// Free text with suggestions from the SELF-LEARNING catalogue — every part a
// salesperson ever typed lives under its car model. With a car picked, that
// model's parts rank first ("what Seltos customers actually ask for"); the
// rest of the catalogue follows. Typing something new is always allowed.
const PartSearchInput = ({ catalog = [], carBrand, carModel, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);

  const { modelParts, allParts } = useMemo(() => {
    const all = new Map();   // lower → display name (dedupe across models)
    let forModel = [];
    for (const b of catalog) {
      for (const m of b.models || []) {
        for (const p of m.parts || []) {
          if (!all.has(p.toLowerCase())) all.set(p.toLowerCase(), p);
        }
        if (carModel && m.name.toLowerCase() === carModel.toLowerCase()
            && (!carBrand || b.name.toLowerCase() === carBrand.toLowerCase())) {
          forModel = m.parts || [];
        }
      }
    }
    return { modelParts: forModel, allParts: [...all.values()] };
  }, [catalog, carBrand, carModel]);

  const q = (value || '').trim().toLowerCase();
  const matches = useMemo(() => {
    const hit = (p) => !q || p.toLowerCase().includes(q);
    const inModel = modelParts.filter(hit);
    const seen = new Set(inModel.map((p) => p.toLowerCase()));
    const others = allParts.filter((p) => hit(p) && !seen.has(p.toLowerCase()));
    return { inModel: inModel.slice(0, 6), others: others.slice(0, 6) };
  }, [q, modelParts, allParts]);

  const hasList = open && (matches.inModel.length > 0 || matches.others.length > 0);
  const pick = (p) => { onChange(p); setOpen(false); };

  return (
    <div className="relative">
      <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        placeholder="Side mirror, Engine, Bumper…"
        className={`${inputCls} pl-9`}
      />
      {hasList && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-slate-100 bg-white py-1 shadow-xl">
          {matches.inModel.length > 0 && (
            <p className="px-3.5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-violet-500">
              {carModel} customers ask for
            </p>
          )}
          {matches.inModel.map((p) => (
            <button
              key={`m-${p}`}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(p); }}
              className="w-full px-3.5 py-2 text-left text-sm font-bold text-slate-800 transition hover:bg-violet-50"
            >
              {p}
            </button>
          ))}
          {matches.others.length > 0 && matches.inModel.length > 0 && (
            <p className="border-t border-slate-50 px-3.5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">
              Other parts
            </p>
          )}
          {matches.others.map((p) => (
            <button
              key={`o-${p}`}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(p); }}
              className="w-full px-3.5 py-2 text-left text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Year pickers — a simple dropdown beats typing a year. "Any" = no limit.
const YEARS = (() => {
  const now = new Date().getFullYear();
  const list = [];
  for (let y = now; y >= 1985; y -= 1) list.push(y);
  return list;
})();

// ── Add / edit a batch ───────────────────────────────────────────────────────
const BatchModal = ({ batch, catalog, busy, error, onClose, onSave }) => {
  const editing = Boolean(batch?.id);
  const [form, setForm] = useState({
    part_name: batch?.part_name || '',
    car_brand: batch?.car_brand || '',
    car_model: batch?.car_model || '',
    year_from: batch?.year_from || '',
    year_to: batch?.year_to || '',
    variant: batch?.variant || '',
    quantity: batch?.quantity || '',
    unit_cost: batch?.unit_cost || '',
    selling_price: batch?.selling_price || '',
    vendor: batch?.vendor || '',
    purchase_date: batch?.purchase_date || new Date().toISOString().slice(0, 10),
    note: batch?.note || '',
  });
  const [localErr, setLocalErr] = useState('');

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const totalCost = useMemo(() => {
    const q = Number(form.quantity);
    const c = Number(form.unit_cost);
    return Number.isFinite(q) && Number.isFinite(c) && q > 0 && c > 0 ? q * c : null;
  }, [form.quantity, form.unit_cost]);

  const marginEach = useMemo(() => {
    const c = Number(form.unit_cost);
    const s = Number(form.selling_price);
    return Number.isFinite(c) && Number.isFinite(s) && s > 0 ? s - c : null;
  }, [form.unit_cost, form.selling_price]);

  const submit = () => {
    setLocalErr('');
    if (!form.part_name.trim()) { setLocalErr('What part is this? e.g. Side mirror'); return; }
    const qty = Number(form.quantity);
    if (!Number.isFinite(qty) || qty < 1) { setLocalErr('How many units did you buy?'); return; }
    const cost = Number(form.unit_cost);
    if (!Number.isFinite(cost) || cost < 0) { setLocalErr('Enter what one unit cost you'); return; }
    if (form.year_from && form.year_to && Number(form.year_from) > Number(form.year_to)) {
      setLocalErr('The "to" year cannot be before the "from" year'); return;
    }

    const body = {
      part_name: form.part_name.trim(),
      car_brand: form.car_brand.trim(),
      car_model: form.car_model.trim(),
      year_from: form.year_from ? Number(form.year_from) : null,
      year_to: form.year_to ? Number(form.year_to) : null,
      variant: form.variant,
      quantity: qty,
      unit_cost: cost,
      selling_price: form.selling_price === '' ? null : Number(form.selling_price),
      vendor: form.vendor.trim(),
      purchase_date: form.purchase_date,
      note: form.note.trim(),
    };
    onSave(body);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              {editing ? 'Edit stock batch' : 'Add outside parts to stock'}
            </h3>
            <p className="mt-0.5 text-sm text-slate-500">
              Bought a batch of the same part — say 15 side mirrors — from outside.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <FiX className="h-4 w-4" />
          </button>
        </div>

        {(error || localErr) && <div className="mt-3"><Alert type="error" message={error || localErr} /></div>}

        <div className="mt-4 space-y-4">
          <Field label="Part" hint="Exactly what a customer would ask for — the match is on this name.">
            <PartSearchInput
              catalog={catalog}
              carBrand={form.car_brand}
              carModel={form.car_model}
              value={form.part_name}
              disabled={busy}
              onChange={(v) => setForm((prev) => ({ ...prev, part_name: v }))}
            />
          </Field>

          {/* Car fit — this is what makes the stock findable from an inquiry */}
          <div className="rounded-xl border border-violet-100 bg-violet-50/50 p-3">
            <p className="text-xs font-bold uppercase tracking-wide text-violet-700">Which car does it fit?</p>
            <p className="mt-0.5 text-[11px] leading-4 text-violet-600">
              Search the catalogue and pick the car — inquiries for that car will be offered this
              stock automatically. Leave it empty to fit every car.
            </p>
            <div className="mt-3 space-y-3">
              <CarSearchPicker
                catalog={catalog}
                brand={form.car_brand}
                model={form.car_model}
                disabled={busy}
                onPick={(carBrand, carModel) =>
                  setForm((prev) => ({ ...prev, car_brand: carBrand, car_model: carModel }))}
              />
              <div className="grid grid-cols-3 gap-3">
                {/* Years: type it or pick it from the styled list */}
                <Field label="Year from" hint="type or pick">
                  <ComboInput
                    numeric
                    value={form.year_from}
                    options={YEARS}
                    disabled={busy}
                    placeholder="Any"
                    onChange={(v) => setForm((prev) => ({ ...prev, year_from: v }))}
                  />
                </Field>
                <Field label="Year to" hint="type or pick">
                  <ComboInput
                    numeric
                    value={form.year_to}
                    options={YEARS.filter((y) => !form.year_from || y >= Number(form.year_from))}
                    disabled={busy}
                    placeholder="Any"
                    onChange={(v) => setForm((prev) => ({ ...prev, year_to: v }))}
                  />
                </Field>
                <Field label="Variant">
                  <Select
                    value={form.variant}
                    options={VARIANTS}
                    disabled={busy}
                    onChange={(v) => setForm((prev) => ({ ...prev, variant: v }))}
                  />
                </Field>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Units bought">
              <NumberInput decimals={false} value={form.quantity} disabled={busy} placeholder="15"
                onChange={(v) => setForm((prev) => ({ ...prev, quantity: v }))} />
            </Field>
            <Field label="Cost per unit">
              <NumberInput value={form.unit_cost} disabled={busy} placeholder="2200"
                onChange={(v) => setForm((prev) => ({ ...prev, unit_cost: v }))} />
            </Field>
          </div>

          {totalCost != null && (
            <p className="-mt-1 text-xs font-semibold text-slate-500">
              Total invested: <span className="text-slate-900">{fmtMoney(totalCost)}</span>
            </p>
          )}

          <Field
            label="Selling price per unit"
            hint={marginEach != null
              ? `Supervisors see this as the suggested quote — ${fmtMoney(marginEach)} margin each.`
              : 'Optional — supervisors see this as the suggested quote.'}
          >
            <NumberInput value={form.selling_price} disabled={busy} placeholder="3500"
              onChange={(v) => setForm((prev) => ({ ...prev, selling_price: v }))} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Bought from">
              <input type="text" value={form.vendor} onChange={set('vendor')} disabled={busy}
                placeholder="Vendor name" className={inputCls} />
            </Field>
            <Field label="Purchase date">
              <input type="date" value={form.purchase_date} onChange={set('purchase_date')} disabled={busy}
                className={inputCls} />
            </Field>
          </div>

          <Field label="Note">
            <textarea rows={2} value={form.note} onChange={set('note')} disabled={busy}
              placeholder="Anything worth remembering about this buy" className={`${inputCls} resize-none`} />
          </Field>
        </div>

        <div className="mt-5 flex gap-3">
          <Button variant="secondary" onClick={onClose} disabled={busy} className="flex-1">Cancel</Button>
          <Button onClick={submit} loading={busy} disabled={busy} className="flex-1">
            {editing ? 'Save changes' : 'Add to stock'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── One batch: money picture + sale ledger ───────────────────────────────────
const DetailModal = ({ row, busy, error, onClose, onAddSale, onRemoveSale, onEdit, onToggleActive, onDeleteBatch }) => {
  const [qty, setQty] = useState('1');
  const [amount, setAmount] = useState(row.selling_price || '');
  const [customer, setCustomer] = useState('');
  const [soldAt, setSoldAt] = useState('');
  const [localErr, setLocalErr] = useState('');

  const pnl = row.pnl || {};
  const sales = pnl.sales || [];
  const left = pnl.quantity_left ?? row.quantity_left ?? 0;
  const reserved = row.reserved ?? 0;

  const addSale = async () => {
    setLocalErr('');
    const q = Number(qty);
    const a = Number(amount);
    if (!Number.isFinite(q) || q < 1) { setLocalErr('How many units went out?'); return; }
    if (q > left) { setLocalErr(`Only ${left} left in this batch`); return; }
    if (!Number.isFinite(a) || a <= 0) { setLocalErr('Enter what you got for it'); return; }
    const body = { quantity: q, amount: a };
    if (customer.trim()) body.customer_name = customer.trim();
    if (soldAt) body.sold_at = soldAt;
    const ok = await onAddSale(body);
    if (ok) { setQty('1'); setAmount(row.selling_price || ''); setCustomer(''); setSoldAt(''); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-slate-900">{row.part_name}</h3>
            <p className="mt-0.5 text-sm text-slate-500">
              {[row.car_brand, row.car_model].filter(Boolean).join(' ') || 'Fits any car'}
              {row.year_label ? ` · ${row.year_label}` : ''}
              {row.variant_display && row.variant ? ` · ${row.variant_display}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <FiX className="h-4 w-4" />
          </button>
        </div>

        {error && <div className="mt-3"><Alert type="error" message={error} /></div>}

        {/* Stock position */}
        <div className="mt-4 rounded-xl border border-slate-200 p-3">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">On the shelf</p>
              <p className="text-2xl font-black text-slate-900">
                {left}<span className="text-base font-bold text-slate-400">/{pnl.quantity ?? row.quantity}</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Cash still on the shelf</p>
              <p className="text-lg font-bold text-amber-600">{fmtMoney(pnl.unsold_value)}</p>
            </div>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pnl.progress_pct ?? 0}%` }} />
          </div>
          <p className="mt-1.5 text-[11px] text-slate-500">
            {fmtMoney(pnl.recovered)} recovered of {fmtMoney(pnl.investment)} invested
            {pnl.break_even ? ' — fully recovered, the rest is profit' : ''}
          </p>
          {reserved > 0 && (
            <p className="mt-1.5 rounded-lg bg-sky-50 px-2.5 py-1.5 text-[11px] font-semibold text-sky-700">
              {reserved} unit{reserved === 1 ? '' : 's'} already quoted to a customer — {row.available ?? 0} free to sell.
            </p>
          )}
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Invested</p>
            <p className="mt-0.5 text-sm font-bold text-rose-600">{fmtMoney(pnl.investment)}</p>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Recovered</p>
            <p className="mt-0.5 text-sm font-bold text-emerald-600">{fmtMoney(pnl.recovered)}</p>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Profit</p>
            <p className="mt-0.5 text-sm font-bold text-emerald-600">{fmtMoney(pnl.profit)}</p>
          </div>
        </div>

        {/* Sale ledger */}
        <div className="mt-4 rounded-xl border border-slate-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Sales out of this batch</p>
            <p className="text-sm font-bold text-slate-900">{fmtMoney(pnl.recovered)}</p>
          </div>
          {sales.length > 0 ? (
            <ul className="mb-3 space-y-1.5">
              {sales.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span className="font-semibold text-slate-800">×{s.quantity}</span>
                  <span className="flex-1 truncate text-xs text-slate-400">
                    {s.sold_at_display}
                    {s.customer_name ? ` · ${s.customer_name}` : ''}
                    {s.auto ? ` · from Inquiry #${s.inquiry_id}` : ''}
                    {s.note && !s.auto ? ` · ${s.note}` : ''}
                  </span>
                  <span className="font-bold text-slate-900">{fmtMoney(s.amount)}</span>
                  {s.auto ? (
                    <span className="rounded px-1.5 py-0.5 text-[10px] font-bold text-slate-400" title="Recorded automatically when the order was dispatched">
                      AUTO
                    </span>
                  ) : (
                    <button type="button" onClick={() => onRemoveSale(s.id)} disabled={busy}
                      className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50" title="Remove">
                      <FiX className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-3 text-xs text-slate-400">
              Nothing sold yet. Sales through an inquiry record themselves on dispatch — use the box
              below only for counter/walk-in sales.
            </p>
          )}

          {localErr && <p className="mb-2 text-xs font-semibold text-rose-600">{localErr}</p>}
          {left > 0 ? (
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-16">
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">Units</span>
                <NumberInput
                  decimals={false} value={qty} disabled={busy} onChange={setQty}
                  className="w-full rounded-xl border border-slate-200 px-2 py-2 text-sm font-semibold focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                />
              </div>
              <div className="relative w-28">
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">Got</span>
                <span className="pointer-events-none absolute left-3 top-[30px] z-10 text-sm font-semibold text-slate-400">₹</span>
                <NumberInput
                  value={amount} disabled={busy} onChange={setAmount}
                  className="w-full rounded-xl border border-slate-200 py-2 pl-7 pr-2 text-sm font-semibold focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                />
              </div>
              <div className="min-w-0 flex-1">
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">Customer</span>
                <input type="text" value={customer} onChange={(e) => setCustomer(e.target.value)} disabled={busy}
                  placeholder="walk-in" className={inputCls} />
              </div>
              <div>
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">Date</span>
                <input type="date" value={soldAt} onChange={(e) => setSoldAt(e.target.value)} disabled={busy}
                  className="rounded-xl border border-slate-200 px-2 py-2 text-sm text-slate-600 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300" />
              </div>
              <Button type="button" variant="secondary" onClick={addSale} loading={busy} disabled={busy}>Record</Button>
            </div>
          ) : (
            <p className="rounded-lg bg-slate-50 px-3 py-2.5 text-center text-xs font-semibold text-slate-500">
              Sold out — every unit is gone.
            </p>
          )}
        </div>

        {row.vendor || row.note ? (
          <p className="mt-3 text-xs text-slate-500">
            {row.vendor ? <>Bought from <b className="text-slate-700">{row.vendor}</b>{row.purchase_date_display ? ` on ${row.purchase_date_display}` : ''}. </> : null}
            {row.note}
          </p>
        ) : null}

        <div className="mt-5 flex gap-3">
          <Button variant="secondary" onClick={() => onToggleActive(!row.is_active)} disabled={busy} className="flex-1">
            {row.is_active ? 'Take off the shelf' : 'Put back on the shelf'}
          </Button>
          <Button onClick={onEdit} disabled={busy} className="flex-1">Edit batch</Button>
        </div>
        {!row.is_active && (
          <p className="mt-2 text-center text-[11px] font-semibold text-amber-600">
            Off the shelf — supervisors are not offered this stock on new inquiries.
          </p>
        )}
        {/* Delete only exists while the batch has no sales — after that it is a
            financial record and the backend refuses (deactivate instead). */}
        {sales.length === 0 && (
          <button
            type="button"
            onClick={onDeleteBatch}
            disabled={busy}
            className="mt-3 w-full text-center text-xs font-semibold text-slate-400 transition hover:text-rose-600"
          >
            Delete this batch (nothing sold yet)
          </button>
        )}
      </div>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const OutsidePartsPage = () => {
  const [tab, setTab] = useState('in_stock');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState('');
  const [editing, setEditing] = useState(null);      // batch object or {} for new
  const [detail, setDetail] = useState(null);        // batch id being viewed
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Car catalogue for the add-batch search picker — same list the Stock
  // Inventory screen uses. Loaded once; the picker degrades to custom-typing
  // if it hasn't arrived.
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    let active = true;
    stockApi.getCarCatalog()
      .then((res) => { if (active) setCatalog(res.data ?? res ?? []); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      const res = await outsidePartApi.getList(params);
      setData(res.data ?? res);
    } catch (e) {
      setError(errText(e, 'Could not load outside part stock'));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setModalError('');
    setBusy(true);
    try {
      const res = await outsidePartApi.getDetail(id);
      setDetail(res.data ?? res);
    } catch (e) {
      setError(errText(e, 'Could not open that batch'));
    } finally { setBusy(false); }
  };

  const refreshDetail = async (id) => {
    const res = await outsidePartApi.getDetail(id);
    setDetail(res.data ?? res);
  };

  const save = async (body) => {
    setBusy(true); setModalError('');
    try {
      if (editing?.id) await outsidePartApi.update(editing.id, body);
      else await outsidePartApi.create(body);
      setEditing(null);
      if (detail?.id) await refreshDetail(detail.id);
      load();
    } catch (e) {
      setModalError(errText(e, 'Could not save this batch'));
    } finally { setBusy(false); }
  };

  const addSale = async (body) => {
    setBusy(true); setModalError('');
    try {
      await outsidePartApi.addSale(detail.id, body);
      await refreshDetail(detail.id);
      load();
      return true;
    } catch (e) {
      setModalError(errText(e, 'Could not record that sale'));
      return false;
    } finally { setBusy(false); }
  };

  const removeSale = async (saleId) => {
    setBusy(true); setModalError('');
    try {
      await outsidePartApi.removeSale(detail.id, saleId);
      await refreshDetail(detail.id);
      load();
    } catch (e) {
      setModalError(errText(e, 'Could not remove that sale'));
    } finally { setBusy(false); }
  };

  const deleteBatch = async ({ reason }) => {
    setBusy(true); setModalError('');
    try {
      await outsidePartApi.remove(deleteTarget.id, { reason });
      setDeleteTarget(null);
      setDetail(null);
      load();
    } catch (e) {
      setModalError(errText(e, 'Could not delete this batch'));
    } finally { setBusy(false); }
  };

  const toggleActive = async (isActive) => {
    setBusy(true); setModalError('');
    try {
      await outsidePartApi.update(detail.id, { is_active: isActive });
      await refreshDetail(detail.id);
      load();
    } catch (e) {
      setModalError(errText(e, 'Could not update this batch'));
    } finally { setBusy(false); }
  };

  const allRows = useMemo(() => data?.stocks ?? [], [data]);
  const rows = useMemo(() => {
    if (tab === 'in_stock') return allRows.filter((r) => (r.quantity_left ?? 0) > 0);
    if (tab === 'sold_out') return allRows.filter((r) => (r.quantity_left ?? 0) === 0);
    return allRows;
  }, [allRows, tab]);

  const counts = useMemo(() => ({
    in_stock: allRows.filter((r) => (r.quantity_left ?? 0) > 0).length,
    sold_out: allRows.filter((r) => (r.quantity_left ?? 0) === 0).length,
    all: allRows.length,
  }), [allRows]);

  const totals = data?.totals || {};

  return (
    <MainLayout>
      <PageHeader>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load} className="gap-2"><FiRefreshCw className="h-4 w-4" /> Refresh</Button>
          <Button onClick={() => { setModalError(''); setEditing({}); }} className="gap-2">
            <FiPlus className="h-4 w-4" /> Add stock
          </Button>
        </div>
      </PageHeader>

      {error && <div className="mb-4"><Alert type="error" message={error} /></div>}

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={FiBox} label="Units on the shelf" value={totals.units_left ?? '—'}
          hint={totals.units != null ? `of ${totals.units} bought` : ''} tone="amber" />
        <StatCard icon={FiDollarSign} label="Invested" value={fmtMoney(totals.investment)} tone="rose" />
        <StatCard icon={FiTrendingUp} label="Recovered" value={fmtMoney(totals.recovered)} tone="emerald" />
        <StatCard icon={FiLayers} label="Still tied up" value={fmtMoney(totals.unsold_value)}
          hint="cash sitting in unsold units" tone="sky" />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.value} type="button" onClick={() => setTab(t.value)} title={t.hint}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition ${
              tab === t.value
                ? 'border-violet-600 bg-violet-600 text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'
            }`}
          >
            {t.label}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${tab === t.value ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
              {counts[t.value]}
            </span>
          </button>
        ))}
        <div className="relative ml-auto min-w-[220px] flex-1 sm:max-w-xs">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search part, car or vendor"
            className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
          />
        </div>
      </div>

      {loading ? (
        <p className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center">
          <FiBox className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm font-semibold text-slate-700">
            {search ? 'Nothing matches that search' : 'No outside parts in stock'}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            {search ? 'Try a different part, car or vendor.' : 'Bought a batch of the same part? Add it here and inquiries will find it.'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-bold">Part</th>
                  <th className="px-4 py-3 font-bold">Fits</th>
                  <th className="px-4 py-3 text-right font-bold">Left</th>
                  <th className="px-4 py-3 text-right font-bold">Cost / unit</th>
                  <th className="px-4 py-3 text-right font-bold">Selling</th>
                  <th className="px-4 py-3 text-right font-bold">Invested</th>
                  <th className="px-4 py-3 text-right font-bold">Recovered</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const left = r.quantity_left ?? 0;
                  const free = r.available ?? left;
                  return (
                    <tr key={r.id} className="cursor-pointer transition hover:bg-slate-50" onClick={() => openDetail(r.id)}>
                      <td className="px-4 py-3">
                        <p className="font-bold text-slate-900">{r.part_name}</p>
                        <p className="text-[11px] text-slate-400">
                          {r.vendor || 'No vendor noted'}{r.purchase_date_display ? ` · ${r.purchase_date_display}` : ''}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-slate-700">
                          {[r.car_brand, r.car_model].filter(Boolean).join(' ') || <span className="text-slate-400">Any car</span>}
                        </p>
                        <p className="text-[11px] text-slate-400">
                          {r.year_label || 'Any year'}{r.variant ? ` · ${r.variant_display}` : ' · Any variant'}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-black ${left === 0 ? 'text-slate-300' : 'text-slate-900'}`}>{left}</span>
                        <span className="text-slate-400">/{r.quantity}</span>
                        {free !== left && (
                          <p className="text-[11px] font-semibold text-sky-600">{free} free</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">{fmtMoney(r.unit_cost)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">{fmtMoney(r.selling_price)}</td>
                      <td className="px-4 py-3 text-right text-rose-600">{fmtMoney(r.investment)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-bold text-emerald-600">{fmtMoney(r.recovered)}</span>
                        <div className="mt-1 h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${r.progress_pct ?? 0}%` }} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!r.is_active && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                            <FiAlertCircle className="h-3 w-3" /> OFF SHELF
                          </span>
                        )}
                        {r.is_active && Number(r.progress_pct) >= 100 && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                            <FiCheckCircle className="h-3 w-3" /> RECOVERED
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <BatchModal
          batch={editing.id ? editing : null}
          catalog={catalog}
          busy={busy}
          error={modalError}
          onClose={() => { setEditing(null); setModalError(''); }}
          onSave={save}
        />
      )}

      {detail && !editing && (
        <DetailModal
          row={detail}
          busy={busy}
          error={modalError}
          onClose={() => { setDetail(null); setModalError(''); }}
          onAddSale={addSale}
          onRemoveSale={removeSale}
          onToggleActive={toggleActive}
          onEdit={() => { setModalError(''); setEditing(detail); }}
          onDeleteBatch={() => { setModalError(''); setDeleteTarget(detail); }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirm
          title="Delete this stock batch?"
          summary={`${deleteTarget.display_name || deleteTarget.part_name} · ${deleteTarget.quantity} units`}
          consequences={[
            'The batch disappears from the shelf and from inquiry matching.',
            'Nothing has been sold from it, so no money records are affected.',
          ]}
          busy={busy}
          error={modalError}
          onClose={() => { if (!busy) { setDeleteTarget(null); setModalError(''); } }}
          onConfirm={deleteBatch}
        />
      )}
    </MainLayout>
  );
};

export default OutsidePartsPage;
